import React, { useEffect, useState, useMemo } from "react";
import Typography from "@mui/material/Typography";
import Link from "@mui/material/Link";
import BreadcrumbsComponent from "../components/Breadcrumbs";
import { routes } from "../routes/routes";
import useFetchUsers from "../hooks/useFetchUsers";
import DataTableGeneric from "../components/DataTableGeneric";
import { ErrorSnackbar } from "../components/Snackbar/ErrorSnackbar";
import { SuccessSnackbar } from "../components/Snackbar/SuccessSnackbar";
import SearchInput from "../components/SearchInput";
import UpdateUserInfoModal from "../components/Modal/UpdateUserInfoModal";
import { useNavigate } from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";
import { Button, Box } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useStore } from "../store/useStore";
import useToggleUserActive from "../hooks/useToggleUserActive";
import { ToggleButton, ToggleButtonGroup } from "@mui/material";
import ConfirmationModal from "../components/Modal/ConfirmationModal";
import { useUserStore } from "../store/useUserStore";
import { BANNER_COLOR, HOVER_COLOR, TEXT_COLOR } from "./root";

// Configuración de columnas para usuarios
const columns = [
  { id: "fullName", label: "Nombre", minWidth: 250 },
  { id: "email", label: "Email", minWidth: 100 },
  { id: "phone", label: "Teléfono", minWidth: 100 },
  { id: "identification", label: "N.° de identificación", minWidth: 100 },
  { id: "role", label: "Roles", minWidth: 170 },
  {
    id: "state",
    label: "Estado",
    minWidth: 170,
  },
  {
    id: "actions",
    label: "Acciones",
    minWidth: 170,
  },
];

// Función para formatear el número de teléfono
function formatPhone(phone) {
  if (phone && typeof phone === "string" && phone.startsWith("+57")) {
    return phone.replace("+57", "+57 ");
  }
  return phone;
}

// Función para formatear datos de usuario
function createData(fullName, email, phone, identification, role, state, actions) {
  return { fullName, email, phone, identification, role, state, actions };
}

const UsersPage = () => {
  const breadcrumbs = [
    <Link underline="none" key="1" color="inherit" href={routes.root}>
      Página Principal
    </Link>,
    <Typography key="3" color="secondary" sx={{ fontSize: "14px" }}>
      Usuarios
    </Typography>,
  ];

  const { getUsers, data: users, loading, error } = useFetchUsers();
  const [rows, setRows] = useState([]);
  const [successOpen, setSuccessOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();
  const { setShowUserCreatedInfoModal } = useStore();
  const { toggleUserActive, error: toggleError, loading: toggleLoading } = useToggleUserActive();
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [pendingUserId, setPendingUserId] = useState(null);
  const [pendingActivate, setPendingActivate] = useState(null);
  const [pendingToggleUserId, setPendingToggleUserId] = useState(null);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const { userInfo } = useUserStore();
  const userRoles = userInfo?.roles || [];
  const isAdmin = userRoles.includes("admin");

  // Estados para manejar el modal de actualización
  const [selectedUser, setSelectedUser] = useState(null);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);

  const handleToggleUser = (userId, activate) => {
    setPendingUserId(userId);
    setPendingActivate(activate);
    setConfirmModalOpen(true);
  };

  const confirmToggleUser = async () => {
    if (pendingUserId == null || pendingActivate == null) return;

    setPendingToggleUserId(pendingUserId);

    const success = await toggleUserActive({ userId: pendingUserId, activate: pendingActivate });

    if (success) {
      refreshUserList();
      setPendingToggleUserId(null);
      setConfirmModalOpen(false);
      setPendingUserId(null);
      setPendingActivate(null);
    }
  };

  useEffect(() => {
    getUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setIsUpdateModalOpen(true);
  };

  const onInfoUpdateError = (error) => {
    setErrorMessage(error?.message || "Error al actualizar la información del usuario.");
    setShowError(true);
  };

  useEffect(() => {
    if (error || toggleError) {
      const err = error || toggleError;
      setErrorMessage(err?.message || "Ha ocurrido un error inesperado.");
      setShowError(true);
    }
  }, [error, toggleError]);

  useEffect(() => {
    if (users && Array.isArray(users)) {
      const newRows = users.map((user) =>
        createData(
          [user.name, user.last_name].filter(Boolean).join(" "),
          user.email || "",
          formatPhone(user.phone_number || ""),
          user.id_document || "",
          (user.user_roles || []).map((roleObj) => roleObj.role).join(", "),
          isAdmin ? (
            <ToggleButtonGroup
              value={user.active ? "active" : "blocked"}
              exclusive
              size="small"
              onChange={(_, newValue) => {
                if (!newValue || newValue === (user.active ? "active" : "blocked")) return;
                handleToggleUser(user.id, newValue === "active");
              }}
              sx={{
                "& .MuiToggleButton-root": {
                  px: 2,
                  py: 0.5,
                  border: "1px solid",
                  borderColor: "divider",
                  textTransform: "none",
                  fontWeight: 500,
                  "&:not(.Mui-selected)": {
                    "&:hover": { bgcolor: "action.hover" },
                  },
                },
                "& .MuiToggleButtonGroup-grouped": {
                  "&:not(:first-of-type)": {
                    borderLeft: "1px solid",
                    borderColor: "divider",
                  },
                },
              }}
            >
              <ToggleButton
                value="active"
                disabled={toggleLoading && pendingToggleUserId === user.id}
                sx={{
                  "&.Mui-selected": {
                    color: TEXT_COLOR,
                    bgcolor: BANNER_COLOR,
                    "&:hover": { bgcolor: HOVER_COLOR },
                  },
                }}
              >
                {toggleLoading && pendingToggleUserId === user.id && pendingActivate === true ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  "Activo"
                )}
              </ToggleButton>
              <ToggleButton
                value="blocked"
                disabled={toggleLoading && pendingToggleUserId === user.id}
                sx={{
                  "&.Mui-selected": {
                    color: "white",
                    bgcolor: "#e74040",
                    "&:hover": { bgcolor: "#dd3a3a" },
                  },
                }}
              >
                {toggleLoading && pendingToggleUserId === user.id && pendingActivate === false ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  "Desactivado"
                )}
              </ToggleButton>
            </ToggleButtonGroup>
          ) : (
            <Typography variant="body2" color={user.active ? "success.main" : "error.main"}>
              {user.active ? "Activo" : "Desactivado"}
            </Typography>
          ),
          [
            { buttonText: "Editar", onClick: () => handleEditUser(user) },
            { buttonText: "Detalle", onClick: () => navigate(routes.usuarioDetalle.replace(":id", user.id)) },
          ],
        ),
      );
      setRows(newRows);
    }
  }, [users, navigate, toggleLoading, pendingToggleUserId, pendingActivate, isAdmin]);

  // Maneja el cambio en el componente de búsqueda
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  // Filtra las filas por email, teléfono o número de identificación
  const filteredRows = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return rows.filter(
      (row) =>
        (row.email || "").toLowerCase().includes(query) ||
        (row.phone?.toString() || "").includes(query) ||
        (row.identification?.toString() || "").includes(query),
    );
  }, [rows, searchQuery]);

  const refreshUserList = () => {
    localStorage.setItem("forceRefreshUsers", Date.now().toString());
    getUsers(true);
  };

  const onInfoUpdated = () => {
    setSuccessOpen(true);
    refreshUserList();
  };

  return (
    <div className="min-w-full p-10 overflow-x-auto">
      <BreadcrumbsComponent breadcrumbs={breadcrumbs} />
      {/* Uso del componente de búsqueda */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          flexWrap: "wrap",
          gap: 2,
          my: 3,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 250 }}>
          <SearchInput
            id="search-users"
            placeholder="Buscar por email, teléfono o identificación..."
            value={searchQuery}
            onChange={handleSearchChange}
            label="Buscar usuarios"
          />
        </Box>

        <Button
          variant="outlined"
          onClick={() => {
            setShowUserCreatedInfoModal(false);
            navigate(routes.asignacionCrear);
          }}
          startIcon={<AddIcon />}
          sx={{ mt: { xs: 1, sm: 0 }, height: 40 }}
        >
          Crear asignación
        </Button>
        <Button
          variant="outlined"
          onClick={() => {
            setShowUserCreatedInfoModal(false);
            navigate(routes.usuarioCrear);
          }}
          startIcon={<AddIcon />}
          sx={{ mt: { xs: 1, sm: 0 }, height: 40 }}
        >
          Crear usuario
        </Button>
      </Box>
      <div className="mt-6">
        {loading && (
          <div className="flex justify-center items-center h-screen">
            <CircularProgress />
          </div>
        )}
        {error && <p>{error.message}</p>}
        {!loading && !error && <DataTableGeneric columns={columns} rows={filteredRows} />}
      </div>

      {/* Modal para actualizar la información de usuario */}
      {isUpdateModalOpen && selectedUser && (
        <UpdateUserInfoModal
          open={isUpdateModalOpen}
          onClose={() => setIsUpdateModalOpen(false)}
          user={selectedUser}
          onSuccess={onInfoUpdated}
          onError={onInfoUpdateError}
        />
      )}

      <ConfirmationModal
        open={confirmModalOpen}
        handleClose={() => setConfirmModalOpen(false)}
        handleConfirm={confirmToggleUser}
        title={pendingActivate ? "Activar usuario" : "Desactivar usuario"}
        description={pendingActivate ? "¿Está seguro de que desea activar este usuario?" : "¿Está seguro de que desea desactivar este usuario?"}
        confirmText={pendingActivate ? "Activar" : "Desactivar"}
        confirmColor={pendingActivate ? "primary" : "error"}
      />

      <SuccessSnackbar open={successOpen} onClose={() => setSuccessOpen(false)} message="La información fue actualizada correctamente." />
      <ErrorSnackbar open={showError} onClose={() => setShowError(false)} title="Error" message={errorMessage} />
    </div>
  );
};

export default UsersPage;
